import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Phone, FileText, HelpCircle, ChevronDown, Ticket, ClipboardList } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import { UserRole } from '@/constants/roles';
import useSupportStore from '@/stores/Common/SupportStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import './SupportAndHelpPage.css';

const SupportAndHelpPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<string>('All');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  const {
    faqs,
    loading,
    error,
    fetchFAQs,
  } = useSupportStore();

  // Fetch FAQs on mount
  useEffect(() => {
    if (!faqs) {
      fetchFAQs();
    }
  }, [faqs, fetchFAQs]);

  // Get role-based route prefix
  const getRoutePrefix = (): string => {
    switch (user?.role) {
      case UserRole.TRAINEE:
        return '/trainee';
      case UserRole.ADMIN:
        return '/admin';
      case UserRole.TRAINER:
        return '/trainer';
      case UserRole.SUPER_ADMIN:
        return '/superadmin';
      default:
        return '/trainee';
    }
  };

  const routePrefix = getRoutePrefix();

  const categories = ['All', 'Account', 'Billing', 'Security', 'General', 'Support'];

  const filteredFAQs = activeTab === 'All'
    ? (faqs || [])
    : (faqs || []).filter(faq => faq.category === activeTab);

  const toggleFAQ = (id: number) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  const handleNavigation = (path: string) => {
    setIsNavigating(true);
    navigate(path);
  };

  const getCategoryColor = (category: string): string => {
    const colors: { [key: string]: string } = {
      Account: '#8b5cf6',
      Billing: '#8b5cf6',
      Security: '#8b5cf6',
      General: '#8b5cf6',
      Support: '#8b5cf6',
    };
    return colors[category] || '#8b5cf6';
  };

  if (isNavigating) {
    return (
      <div className="support-container">
        <LoadingSpinner fullScreen size="lg" />
      </div>
    );
  }

  return (
    <div className="support-container">
      <div className="support-header">
        <h1 className="support-title">Contact & Support</h1>
        <p className="support-subtitle">
          Choose the best way to reach us. Our support team is ready to assist you with any questions or concerns.
        </p>
      </div>

      <div className="support-cards-container">
        <div className="support-card">
          <div className="support-icon-wrapper">
            <MessageCircle size={28} color="#fff" />
          </div>
          <h3 className="support-card-title">Chat with Us</h3>
          <p className="support-card-description">
            Get instant support through our live chat. Our team is available 24/7 to help you.
          </p>
          <button className="support-card-button" onClick={() => handleNavigation(`${routePrefix}/support/chat`)}>
            Start chatting <span className="support-arrow">→</span>
          </button>
        </div>

        <div className="support-card">
          <div className="support-icon-wrapper">
            <Phone size={28} color="#fff" />
          </div>
          <h3 className="support-card-title">Call Us</h3>
          <p className="support-card-description">
            Speak directly with our support team. We're ready to assist you over the phone.
          </p>
          <button className="support-card-button" onClick={() => handleNavigation(`${routePrefix}/support/call`)}>
            View details <span className="support-arrow">→</span>
          </button>
        </div>

        <div className="support-card">
          <div className="support-icon-wrapper">
            <FileText size={28} color="#fff" />
          </div>
          <h3 className="support-card-title">Write Your Query</h3>
          <p className="support-card-description">
            Submit a detailed query and our management team will respond within 24 hours.
          </p>
          <button className="support-card-button" onClick={() => handleNavigation(`${routePrefix}/support/query`)}>
            Submit query <span className="support-arrow">→</span>
          </button>
        </div>

        <div className="support-card">
          <div className="support-icon-wrapper">
            <ClipboardList size={28} color="#fff" />
          </div>
          <h3 className="support-card-title">My Tickets</h3>
          <p className="support-card-description">
            View and track your support tickets. Check status updates and replies from our team.
          </p>
          <button className="support-card-button" onClick={() => handleNavigation(`${routePrefix}/support/query`)}>
            View my tickets <span className="support-arrow">→</span>
          </button>
        </div>

        {(user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.TRAINER) && (
          <div className="support-card">
            <div className="support-icon-wrapper">
              <Ticket size={28} color="#fff" />
            </div>
            <h3 className="support-card-title">Manage Tickets</h3>
            <p className="support-card-description">
              {user?.role === UserRole.TRAINER
                ? 'View and manage support tickets assigned to you. Respond and update ticket status.'
                : 'View and manage all support tickets raised by users. Respond, assign, and update ticket status.'}
            </p>
            <button className="support-card-button" onClick={() => handleNavigation(`${routePrefix}/support/tickets`)}>
              View all tickets <span className="support-arrow">→</span>
            </button>
          </div>
        )}
      </div>

      <div className="support-faq-section">
        <div className="support-faq-icon-wrapper">
          <HelpCircle size={32} color="#fff" />
        </div>
        <h2 className="support-faq-title">Frequently Asked Questions</h2>
        <p className="support-faq-subtitle">Quick answers to questions you may have</p>

        <div className="support-tabs-container">
          {categories.map((category) => (
            <button
              key={category}
              className={`support-tab ${activeTab === category ? 'active' : ''}`}
              onClick={() => setActiveTab(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {loading && !faqs ? (
          <div style={{ marginTop: '2rem' }}>
            <LoadingSpinner size="md" />
          </div>
        ) : error && !faqs ? (
          <div style={{ marginTop: '2rem' }}>
            <ErrorDisplay
              title="Failed to Load FAQs"
              message={error}
              type="error"
              showRetry
              onRetry={fetchFAQs}
            />
          </div>
        ) : (
          <div className="support-faq-list">
            {filteredFAQs.map((faq) => (
              <div key={faq.id} className="support-faq-item">
                <button
                  className="support-faq-button"
                  onClick={() => toggleFAQ(faq.id)}
                >
                  <div className="support-faq-button-content">
                    <span
                      className="support-category-badge"
                      style={{ backgroundColor: getCategoryColor(faq.category) }}
                    >
                      {faq.category}
                    </span>
                    <span className="support-faq-question">{faq.question}</span>
                  </div>
                  <ChevronDown
                    size={20}
                    className={`support-chevron ${expandedFAQ === faq.id ? 'expanded' : ''}`}
                  />
                </button>
                {expandedFAQ === faq.id && (
                  <div className="support-faq-answer">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportAndHelpPage;
